import type { IxmUserauthV1JoinedRealmAccess } from '@interxfi/types';
import { useRealmAuthActiveAccess, useRealmAuthContext } from '../context';
import type { WhoAmIContext } from '../realm-auth/store';
import { TenantContext } from '../realm-context';
import { useGetProgramTenant } from './get-program-tenant';

export function getProgramMetaAccess(
  programId: string | undefined,
  ctx: WhoAmIContext | undefined,
  activeAccess: IxmUserauthV1JoinedRealmAccess | undefined,
  allowProgramOrClearingAccess: boolean = false,
) {
  const programOrganizationId = ctx?.accesses?.find(
    (access) => access.tenant?.tenantType === TenantContext.Program && access.tenant?.tenantId === programId,
  )?.access?.organizationId;

  if (activeAccess?.tenant?.tenantType === TenantContext.Platform) {
    return activeAccess;
  }

  if (!programOrganizationId && activeAccess?.tenant?.tenantType === TenantContext.Organization) {
    return activeAccess;
  }

  if (allowProgramOrClearingAccess && activeAccess?.tenant?.tenantType === TenantContext.Program && activeAccess.tenant?.tenantId === programId) {
    return activeAccess;
  }

  if (allowProgramOrClearingAccess && activeAccess?.tenant?.tenantType === TenantContext.Clearing) {
    return activeAccess;
  }

  if (
    programOrganizationId &&
    activeAccess?.tenant?.tenantType === TenantContext.Organization &&
    activeAccess?.tenant?.tenantId === programOrganizationId
  ) {
    return activeAccess;
  }

  const platformAccess = ctx?.accesses?.find((access) => access.tenant?.tenantType === TenantContext.Platform);
  if (platformAccess) {
    return platformAccess;
  }

  if (programOrganizationId) {
    const orgAccess = ctx?.accesses?.find(
      (access) => access.tenant?.tenantType === TenantContext.Organization && access.tenant?.tenantId === programOrganizationId,
    );

    return orgAccess;
  }

  if (allowProgramOrClearingAccess) {
    const clearingAccess = ctx?.accesses?.find((access) => access.tenant?.tenantType === TenantContext.Clearing);
    if (clearingAccess) {
      return clearingAccess;
    }

    const programAccess = ctx?.accesses?.find(
      (access) => access.tenant?.tenantType === TenantContext.Program && access.tenant?.tenantId === programId,
    );
    if (programAccess) {
      return programAccess;
    }
  }

  return undefined;
}

export function useProgramMetaAccessSelector(
  programId: string | undefined,
  allowProgramOrClearingAccess: boolean = false,
): IxmUserauthV1JoinedRealmAccess | undefined {
  const activeAccess = useRealmAuthActiveAccess();
  const ctx = useRealmAuthContext();

  return getProgramMetaAccess(programId, ctx, activeAccess, allowProgramOrClearingAccess);
}

export function useProgramAccess(programId: string | undefined, allowClearingAccess: boolean = false) {
  const programTenant = useGetProgramTenant(programId ? { tenantId: programId } : undefined);
  const activeAccess = useRealmAuthActiveAccess();
  const ctx = useRealmAuthContext();

  if (
    (activeAccess?.realm?.realmId === programTenant?.data?.tenant?.realmId &&
      activeAccess?.tenant?.tenantType === TenantContext.Clearing &&
      allowClearingAccess) ||
    (programId && activeAccess?.tenant?.tenantType === TenantContext.Program && activeAccess.tenant?.tenantId === programId)
  ) {
    return activeAccess;
  }

  const programAccess = programId
    ? ctx?.accesses?.find(
        (access) =>
          access.tenant?.tenantType === TenantContext.Program &&
          access.tenant?.tenantId === programId &&
          access?.realm?.realmId === programTenant?.data?.tenant?.realmId,
      )
    : undefined;
  if (programAccess) {
    return programAccess;
  }

  if (allowClearingAccess) {
    const clearingAccess = ctx?.accesses?.find(
      (access) => access.tenant?.tenantType === TenantContext.Clearing && access?.realm?.realmId === programTenant?.data?.tenant?.realmId,
    );
    if (clearingAccess) {
      return clearingAccess;
    }
  }

  return undefined;
}
